import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { muscleGroups } from "../data/muscleGroups";
import { getWeekdayLabel, getMonthDayLabel } from "../utils/formatDateLabels";
import { useI18n } from "../i18n/i18n";
import { getLocalizedExerciseName } from "../utils/exerciseName";
import { getAllExercisesCatalog } from "../services/customExerciseService";

const buildExerciseLookup = () => Object.fromEntries(getAllExercisesCatalog().map((item) => [item.id, item]));

const truncateToTwoWords = (text) => {
    if (typeof text !== "string") {
        return text;
    }

    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length <= 2) {
        return text;
    }

    return `${words.slice(0, 2).join(" ")}...`;
};

const getMuscleImageFilename = (groupId) => {
    const overrides = {
        hip_flexors: "Hip_Flexor",
    };

    const baseName = overrides[groupId]
        ? overrides[groupId]
        : groupId
            .split("_")
            .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
            .join("_");

    return `${baseName}-54.png`;
};

const getMuscleImageWebpFilename = (groupId) => getMuscleImageFilename(groupId).replace(".png", ".webp");

function collectExercises({ date, groupId, exerciseLog, exerciseMap, t }) {
    return Object.entries(exerciseLog[date] || {})
        .reduce((acc, [exerciseId]) => {
            const definition = exerciseMap[exerciseId];
            const groupImpact = definition?.muscleGroups.find((mg) => mg.group === groupId);

            if (!groupImpact) {
                return acc;
            }

            const fallbackName = t ? t("home.unknownExercise", { id: exerciseId }) : `Unknown (${exerciseId})`;
            acc.push({
                id: exerciseId,
                date,
                name: definition ? getLocalizedExerciseName(definition, t) : fallbackName,
                priority: groupImpact.priority || 'primary',
            });

            return acc;
        }, []);
}

function ExerciseLinks({ exercises, localizePath }) {
    if (exercises.length === 0) {
        return null;
    }

    return (
        <div className="exerciseGroup">
            {exercises.map(({ id, date, name, priority }) => (
                <Link
                    key={`${id}-${date}`}
                    to={localizePath(`/LogExercise/${id}/${date}`)}
                    className={`exerciseLink${priority === 'secondary' ? ' exerciseLink--indirect' : ''}`}
                >
                    <span>{truncateToTwoWords(name)}</span>
                </Link>
            ))}
        </div>
    );
}

export default function WorkoutProgressTable({ dates, exerciseLog, showIndirect = true }) {
    const { t, locale, localizePath } = useI18n();
    const exerciseMap = useMemo(buildExerciseLookup, []);
    const columns = useMemo(
        () =>
            dates.map((date) => ({
                key: date,
                weekday: getWeekdayLabel(date, locale),
                monthDay: getMonthDayLabel(date, locale),
            })),
        [dates, locale]
    );

    return (
        <table className="muscleTable" aria-label={t("home.workoutTracker")}>
            <thead>
            <tr>
                <th>{t("home.muscleGroup")}</th>
                {columns.map(({ key, weekday, monthDay }) => (
                    <th key={key} className="dateHeader">
                        <div className="dateHeaderWeekday">{weekday}</div>
                        <div className="dateHeaderDate">{monthDay}</div>
                    </th>
                ))}
            </tr>
            </thead>
            <tbody>
            {muscleGroups.map((group) => (
                <tr key={group.id}>
                    <td className="muscleGroupCell">
                        <div className="muscleGroupHeader">
                            <span className="muscleGroupName">
                                {t(`muscleGroups.${group.id}`) || group.name}
                            </span>
                            <Link
                                to={localizePath(`/SelectExercise/${group.id}`)}
                                className="addExerciseButton addExerciseButton--muscleGroup"
                                aria-label={t("home.addExerciseFor", {
                                    group: t(`muscleGroups.${group.id}`) || group.name,
                                })}
                            >
                                <span aria-hidden="true">+</span>
                            </Link>
                        </div>
                        <div className="muscleGroupIconRow">
                            <picture>
                                <source
                                    type="image/webp"
                                    srcSet={`/images/muscles/${getMuscleImageWebpFilename(group.id)} 1x, /images/muscles/${getMuscleImageWebpFilename(group.id).replace('-54.webp', '-108.webp')} 2x`}
                                />
                                <img
                                    src={`/images/muscles/${getMuscleImageFilename(group.id)}`}
                                    srcSet={`/images/muscles/${getMuscleImageFilename(group.id)} 1x, /images/muscles/${getMuscleImageFilename(group.id).replace('-54.png', '-108.png')} 2x`}
                                    alt={t(`muscleGroups.${group.id}`) || group.name}
                                    className="muscleGroupImage"
                                    loading="lazy"
                                    width="54"
                                    height="54"
                                />
                            </picture>
                        </div>
                    </td>
                    {columns.map(({ key }) => {
                        const exercises = collectExercises({
                            date: key,
                            groupId: group.id,
                            exerciseLog,
                            exerciseMap,
                            t,
                        });
                        const filtered = showIndirect ? exercises : exercises.filter((e) => e.priority !== 'secondary');

                        return (
                            <td key={`${group.id}-${key}`}>
                                <ExerciseLinks exercises={filtered} localizePath={localizePath} />
                            </td>
                        );
                    })}
                </tr>
            ))}
            </tbody>
        </table>
    );
}
